// MaturaGeometricRatioClosedTask.js
// Zadanie maturalne – zamknięte (ciąg geometryczny):
// z warunku a_s = K · a_t wyznaczamy iloraz q (ABCD)

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = randInt(0, i);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const isClose = (x, y) => Math.abs(x - y) < 1e-9;

/**
 * Ładne formatowanie ilorazu q do LaTeX:
 * - liczby całkowite jako zwykłe liczby (np. -2, 3, 5),
 * - 1/2, 1/3, 1/5 oraz ich wersje z minusem jako ułamki,
 * - w pozostałych (teoretycznie nie powinno się zdarzyć przy tym generatorze)
 *   zwracamy zwykły zapis liczbowy.
 */
function formatRatioLatex(q) {
  if (Number.isInteger(q)) {
    return String(q);
  }

  const abs = Math.abs(q);
  let fracLatex = null;

  if (isClose(abs, 1 / 2)) {
    fracLatex = "\\frac{1}{2}";
  } else if (isClose(abs, 1 / 3)) {
    fracLatex = "\\frac{1}{3}";
  } else if (isClose(abs, 1 / 5)) {
    fracLatex = "\\frac{1}{5}";
  }

  if (fracLatex) {
    return q < 0 ? `-${fracLatex}` : fracLatex;
  }

  // Fallback – dla naszego generatora raczej nieużywany,
  // ale zostawiamy na wszelki wypadek.
  return String(q);
}

// ======================= GENERATOR =======================

function createOriginalTask() {
  const optionValues = [-2, -0.5, 0.5, 2];
  const options = optionValues.map((value, index) => ({
    value,
    latex: formatRatioLatex(value),
    label: LETTERS[index],
    isCorrect: isClose(value, -0.5),
  }));

  return {
    type: "MaturaGeometricRatioClosedTask",
    data: {
      q: -0.5,
      sIndex: 3,
      tIndex: 6,
      exponent: -3,
      K: -8,
      options,
      correctIndex: 1,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 17,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") {
    return createOriginalTask();
  }

  // chcemy, żeby K było potęgą 2, 3 lub 5 z nieparzystym wykładnikiem,
  // a q było odpowiednio "duże" (2, 3, 5) lub "małe" (1/2, 1/3, 1/5),
  // z plusem lub minusem.

  // 1. wybieramy bazę (2, 3, 5)
  const baseAbs = pick([2, 3, 5]);

  // 2. wybieramy nieparzystą wartość wykładnika |e|
  const exponentMag =
    baseAbs === 2 ? pick([1, 3, 5]) : pick([1, 3]);

  // 3. losujemy, czy ciąg jest rosnący (|q| > 1) czy malejący (0 < |q| < 1)
  const direction = pick(["growing", "decaying"]);

  const qAbs =
    direction === "growing" ? baseAbs : 1 / baseAbs;
  const exponent =
    direction === "growing" ? exponentMag : -exponentMag;

  // 4. znak ilorazu
  const sign = pick([1, -1]);
  const q = sign * qAbs;

  // 5. losujemy indeks t, a s = t + e; pilnujemy, żeby 1 ≤ s,t ≤ 10
  let t;
  let s;
  while (true) {
    t = randInt(2, 9); // coś "ze środka"
    s = t + exponent;
    if (s >= 1 && s <= 10) break;
  }

  // 6. stała K w warunku a_s = K · a_t
  //    (z definicji K = q^(s−t) = q^e, będzie całkowita: ±2, ±8, ±32, ±3, ±27, ±5, ±125)
  const K = Math.round(Math.pow(q, exponent));

  // 7. odpowiedzi – poprawna i "zmyłki"
  //    Dla naszego wyboru q, liczby q, -q, 1/q, -1/q są zawsze czterema
  //    różnymi wartościami, więc nie trzeba żadnych sztucznych "dopychaczy".
  const correctValue = q;
  const optionValues = [
    correctValue,
    -correctValue, // zły znak
    1 / correctValue, // odwrotność
    -1 / correctValue, // odwrotność z minusem
  ];

  const optionsShuffled = shuffle(
    optionValues.map((val) => ({
      value: val,
      latex: formatRatioLatex(val),
    }))
  );

  const options = optionsShuffled.map((opt, idx) => ({
    ...opt,
    label: LETTERS[idx],
    isCorrect: isClose(opt.value, correctValue),
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaGeometricRatioClosedTask",
    data: {
      q,
      sIndex: s,
      tIndex: t,
      exponent,
      K,
      options,
      correctIndex,
    },
  };
}

// ======================= RENDER TREŚCI =======================

export function renderTask({ data }) {
  const { sIndex, tIndex, K, options } = data;

  const sLatex = String(sIndex);
  const tLatex = String(tIndex);
  const KLatex = K < 0 ? `(${K})` : String(K);

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Ciąg geometryczny <InlineMath math={'(a_n)'} />, o wszystkich wyrazach
        rzeczywistych różnych od <InlineMath math={'0'} />, jest określony dla
        każdej liczby naturalnej <InlineMath math={'n \\geq 1'} />.
      </p>
      <p>
        Wyrazy tego ciągu spełniają warunek{" "}
        <InlineMath
          math={`a_{${sLatex}} = ${KLatex}\\cdot a_{${tLatex}}`}
        />
        <InlineMath math={'\\text{.}'} />
      </p>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Iloraz ciągu <InlineMath math={'(a_n)'} /> jest równy:
      </p>
      <div style={{ marginTop: 8 }}>
        {options.map((opt) => (
          <div key={opt.label} style={{ marginBottom: 10 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ======================= INPUT (ABCD) =======================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;

  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange?.({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
      value: opt.value,
    });
  };

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

// ======================= SPRAWDZANIE =======================

export function checkAnswer({ data }, userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !Number.isInteger(userAnswer.choiceIndex)
  ) {
    return false;
  }
  return userAnswer.choiceIndex === data.correctIndex;
}

// ======================= POPRAWNA ODPOWIEDŹ =======================

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

// ======================= WYJAŚNIENIE =======================

export function renderExplanation({ data }) {
  const { q, sIndex, tIndex, exponent, K } = data;

  const qLatex = formatRatioLatex(q);
  const sLatex = String(sIndex);
  const tLatex = String(tIndex);
  const eLatex = String(exponent);
  const KLatex = K < 0 ? `(${K})` : String(K);

  const step1 = "a_n = a_1\\,q^{n-1}";
  const step2a = `a_{${sLatex}} = a_1\\,q^{${sLatex}-1},\\quad a_{${tLatex}} = a_1\\,q^{${tLatex}-1}`;
  const step2b = `a_1\\,q^{${sLatex}-1} = ${KLatex}\\cdot a_1\\,q^{${tLatex}-1}`;
  const step2c = `q^{${sLatex}-${tLatex}} = ${KLatex}`;
  const step2d = `q^{${eLatex}} = ${KLatex}`;

  let step3;

  if (exponent === 1) {
    // najprostszy przypadek: q = K
    step3 = `q = ${KLatex}`;
  } else if (exponent > 1) {
    // dodatni wykładnik – klasyczne pierwiastkowanie
    step3 = `q = \\sqrt[${eLatex}]{${KLatex}} = ${qLatex}`;
  } else {
    // ujemny wykładnik – w zadaniu generujemy liczby tak,
    // że i tak dostajemy ładne q (2, 3, 5, 1/2, 1/3, 1/5 z plusem lub minusem),
    // więc w wyjaśnieniu odwołujemy się bezpośrednio do rozwiązania równania:
    step3 = `q^{${eLatex}} = ${KLatex} \\Rightarrow q = ${qLatex}`;
  }

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Krok 1.</strong> Zapisujemy wzór na wyraz ogólny ciągu
        geometrycznego:
      </p>
      <BlockMath math={step1} />

      <p>
        <strong>Krok 2.</strong> Zapisujemy wyrazy{" "}
        <InlineMath math={`a_{${sLatex}}`} /> i{" "}
        <InlineMath math={`a_{${tLatex}}`} /> oraz korzystamy z warunku z
        treści.
      </p>
      <BlockMath math={step2a} />
      <BlockMath math={step2b} />

      <p>
        Dzielimy obie strony równania przez{" "}
        <InlineMath math={`a_1\\,q^{${tLatex}-1}`} /> (ciąg ma wyrazy różne od{" "}
        <InlineMath math={'0'} />):
      </p>

      <BlockMath math={step2c} />
      <BlockMath math={step2d} />

      <p>
        <strong>Krok 3.</strong> Rozwiązujemy równanie względem ilorazu{" "}
        <InlineMath math={'q'} />:
      </p>
      <BlockMath math={step3} />

      <p>
        Zatem iloraz ciągu jest równy{" "}
        <InlineMath math={`${qLatex}\\text{.}`} />
      </p>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer.choiceIndex !== "number" ||
    !userAnswer.label ||
    userAnswer.latex == null
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}
